import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
} from 'firebase/firestore';
import { firestore } from './client';
import type { Beer, FilterOptions } from '../../utils/types/Beer';

export class BeerService {
  private static readonly BEERS_COLLECTION = 'beers';
  private static readonly CATEGORIES_COLLECTION = 'beerCategories';

  /**
   * Busca todas as cervejas do Firebase
   */
  static async getAllBeers(): Promise<Beer[]> {
    try {
      const beersRef = collection(firestore, this.BEERS_COLLECTION);
      const snapshot = await getDocs(beersRef);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Beer[];
    } catch (error) {
      console.error('Erro ao buscar cervejas:', error);
      throw new Error('Falha ao carregar cervejas');
    }
  }

  /**
   * Busca cervejas com filtros aplicados
   */
  static async getFilteredBeers(filters: {
    breweries?: string[];
    styles?: string[];
    priceRange?: { min: number; max: number };
    searchTerm?: string;
    inStock?: boolean;
    featured?: boolean;
    limitCount?: number;
  }): Promise<Beer[]> {
    try {
      const beersRef = collection(firestore, this.BEERS_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Filtro por disponibilidade
      if (filters.inStock !== undefined) {
        constraints.push(where('inStock', '==', filters.inStock));
      }

      // Filtro por featured
      if (filters.featured !== undefined) {
        constraints.push(where('featured', '==', filters.featured));
      }

      // Filtro por cervejaria
      if (filters.breweries && filters.breweries.length > 0) {
        constraints.push(where('brewery', 'in', filters.breweries));
      }

      // Filtro por estilo
      if (filters.styles && filters.styles.length > 0) {
        constraints.push(where('style', 'in', filters.styles));
      }

      // Filtro por faixa de preço
      if (filters.priceRange) {
        if (filters.priceRange.min > 0) {
          constraints.push(where('price', '>=', filters.priceRange.min));
        }
        if (filters.priceRange.max < 1000) {
          constraints.push(where('price', '<=', filters.priceRange.max));
        }
      }

      // Ordenação padrão
      constraints.push(orderBy('featured', 'desc'));
      constraints.push(orderBy('name', 'asc'));

      // Limite de resultados
      if (filters.limitCount) {
        constraints.push(limit(filters.limitCount));
      }

      const q = query(beersRef, ...constraints);
      const snapshot = await getDocs(q);

      let beers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Beer[];

      // Filtro por termo de busca (feito no cliente para suportar busca parcial)
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        beers = beers.filter(
          beer =>
            beer.name.toLowerCase().includes(searchTerm) ||
            beer.brewery.toLowerCase().includes(searchTerm) ||
            beer.style.toLowerCase().includes(searchTerm) ||
            beer.description.toLowerCase().includes(searchTerm),
        );
      }

      return beers;
    } catch (error) {
      console.error('Erro ao buscar cervejas filtradas:', error);
      throw new Error('Falha ao filtrar cervejas');
    }
  }

  /**
   * Busca uma cerveja específica por ID
   */
  static async getBeerById(id: string): Promise<Beer | null> {
    try {
      const beerRef = doc(firestore, this.BEERS_COLLECTION, id);
      const snapshot = await getDoc(beerRef);

      if (snapshot.exists()) {
        return {
          id: snapshot.id,
          ...snapshot.data(),
        } as Beer;
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar cerveja por ID:', error);
      throw new Error('Falha ao carregar cerveja');
    }
  }

  /**
   * Busca cervejas em destaque
   */
  static async getFeaturedBeers(limitCount: number = 6): Promise<Beer[]> {
    return this.getFilteredBeers({
      featured: true,
      inStock: true,
      limitCount,
    });
  }

  /**
   * Busca opções de filtro disponíveis
   */
  static async getFilterOptions(): Promise<FilterOptions> {
    try {
      // Primeiro, tenta buscar das categorias salvas
      const categoriesRef = collection(firestore, this.CATEGORIES_COLLECTION);
      const categoriesSnapshot = await getDocs(categoriesRef);

      if (!categoriesSnapshot.empty) {
        const breweries =
          categoriesSnapshot.docs.find(doc => doc.id === 'breweries')?.data()
            ?.list || [];
        const styles =
          categoriesSnapshot.docs.find(doc => doc.id === 'styles')?.data()
            ?.list || [];
        const priceRangeDoc = categoriesSnapshot.docs
          .find(doc => doc.id === 'priceRange')
          ?.data();
        const priceRange = priceRangeDoc
          ? {
              min: priceRangeDoc.min || 0,
              max: priceRangeDoc.max || 50,
            }
          : { min: 0, max: 50 };

        return {
          breweries,
          styles,
          priceRange,
        };
      }

      // Se não houver categorias salvas, extrai das cervejas existentes
      const beers = await this.getAllBeers();

      const breweries = [...new Set(beers.map(beer => beer.brewery))].sort();
      const styles = [...new Set(beers.map(beer => beer.style))].sort();
      const prices = beers.map(beer => beer.price);
      const priceRange = {
        min: Math.min(...prices),
        max: Math.max(...prices),
      };

      return {
        breweries,
        styles,
        priceRange,
      };
    } catch (error) {
      console.error('Erro ao buscar opções de filtro:', error);
      // Retorna valores padrão em caso de erro
      return {
        breweries: [],
        styles: [],
        priceRange: { min: 0, max: 50 },
      };
    }
  }

  /**
   * Busca cervejas relacionadas (mesmo estilo ou cervejaria)
   */
  static async getRelatedBeers(
    beer: Beer,
    limitCount: number = 4,
  ): Promise<Beer[]> {
    try {
      const beersRef = collection(firestore, this.BEERS_COLLECTION);

      // Busca por mesmo estilo ou cervejaria, excluindo a cerveja atual
      const q = query(
        beersRef,
        where('inStock', '==', true),
        orderBy('featured', 'desc'),
        limit(limitCount * 2), // Busca mais para poder filtrar
      );

      const snapshot = await getDocs(q);
      const beers = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }) as Beer)
        .filter((b: Beer) => b.id !== beer.id) // Remove a cerveja atual
        .filter(
          (b: Beer) => b.style === beer.style || b.brewery === beer.brewery,
        ) // Filtros de relacionamento
        .slice(0, limitCount); // Limita o resultado

      return beers;
    } catch (error) {
      console.error('Erro ao buscar cervejas relacionadas:', error);
      return [];
    }
  }
}
